/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

// Keep these stylesheet imports before any other imports. Their first `@layer`
// declarations establish the cascade order, so loading a view stylesheet first
// could register `legacy` before `layers.css` has a chance to declare the order.
import "./vendor"
import "./index.css"

import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import { createBrowserRouter } from "react-router"
import { RouterProvider } from "react-router/dom"

import { appComponents, ReactAppProvider } from "./app-context"
import { asyncInit } from "./init"
import { AppLayout } from "./ui/app/app-layout"
import { childrenRoutes } from "./ui/app/app-routes"
import { RouteChangeEffect } from "./ui/app/route-change-effect"
import { LoggerToastEffect } from "./ui/logger/logger-toast-effect"

/**
 * Create the application router and run asynchronous initialization before the
 * root route renders.
 */
const router = createBrowserRouter([
    {
        path: "/",
        element: (
            <ReactAppProvider>
                <LoggerToastEffect />
                <RouteChangeEffect />
                <AppLayout />
            </ReactAppProvider>
        ),
        loader: async () => {
            // TODO: Use a localStorage flag to distinguish initialization needed on every
            // URL from initialization that only needs to run during the initial app load.
            await asyncInit(appComponents)

            // The spinner is outside `#root`, so React's first render does not remove it
            // before initialization completes.
            document.getElementById("initial-loader")?.remove()
        },
        children: childrenRoutes
    }
])

/**
 * Mount the application.
 *
 * React omits StrictMode's development-only checks from production, so there is no
 * need to conditionally add it based on `NODE_ENV`.
 */
const root = createRoot(document.getElementById("root") as HTMLElement)
root.render(
    <StrictMode>
        <RouterProvider router={router} />
    </StrictMode>
)
